import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import EmailRegistrationModal from "./components/forms/EmailRegistrationModal";
import useEmailRegistration from "./hooks/useEmailRegistration";

export default function Blog() {
  const { showRegistrationModal, openRegistrationModal, closeRegistrationModal } = useEmailRegistration();
  
  // Effect to scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Sample blog post data (would come from a backend in a real implementation)
  const featuredPost = {
    id: 1,
    title: "How Hiree is Revolutionizing Restaurant Staffing",
    excerpt: "Discover how our platform is connecting qualified staff with restaurants in need, creating a more efficient hospitality ecosystem through innovative technology and data-driven solutions.",
    image: "/asset/images/blog/featured-post.jpg",
    date: "April 28, 2025",
    category: "Industry Insights",
    author: "Sarah Johnson",
    authorImage: "/asset/images/blog/author1.jpg"
  };
  
  const recentPosts = [
    {
      id: 2,
      title: "7 Essential Tips for Job Seekers in the Restaurant Industry in 2025",
      excerpt: "Looking for a job in the competitive restaurant industry? Our comprehensive guide provides seven essential strategies to help you stand out from the crowd and land your dream position.",
      image: "/asset/images/blog/post2.jpg",
      date: "April 25, 2025",
      category: "Career Advice",
      author: "Michael Chen",
      authorImage: "/asset/images/blog/author2.jpg"
    },
    {
      id: 3,
      title: "The Future of Restaurant Staffing: AI, Flexibility, and Human Touch",
      excerpt: "The pandemic transformed the restaurant industry forever. Explore how staffing has evolved with technology, flexible work arrangements, and renewed focus on employee wellbeing and development.",
      image: "/asset/images/blog/post3.jpg",
      date: "April 20, 2025",
      category: "Industry Trends",
      author: "Emma Williams",
      authorImage: "/asset/images/blog/author3.jpg"
    },
    {
      id: 4,
      title: "The Ultimate Guide to Reducing Employee Turnover in Restaurants",
      excerpt: "High turnover rates can devastate restaurant operations and profitability. Our in-depth guide covers everything from compensation strategies to culture building techniques that retain top talent.",
      image: "/asset/images/blog/post4.jpg",
      date: "April 15, 2025",
      category: "Restaurant Management",
      author: "David Rodriguez",
      authorImage: "/asset/images/blog/author4.jpg"
    }
  ];
  
  // Helper function to get image URLs for posts
  const getImageUrlForPost = (index) => {
    const imageUrls = [
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80", // restaurant dining
      "https://images.unsplash.com/photo-1552566626-52f8b828add9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80", // restaurant staff
      "https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80", // restaurant interior
      "https://images.unsplash.com/photo-1590846406792-0adc7f938f1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80" // chef cooking
    ];
    
    return imageUrls[index % imageUrls.length];
  };
  
  // Helper function to get author images
  const getAuthorImageUrl = (index) => {
    const authorImages = [
      "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1760&q=80", // male 1
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1661&q=80", // female 1
      "https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=774&q=80", // female 2
      "https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=774&q=80", // male 2
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=774&q=80" // female 3
    ];
    
    return authorImages[index % authorImages.length];
  };

  return (
    <div className="py-12 px-6 bg-white">
      {/* Blog Header */}
      <div className="max-w-6xl mx-auto mb-12 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Hiree Blog</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Stay up to date with the latest news, trends, and insights from Hiree and the restaurant industry.
        </p>
      </div>
      
      {/* Featured Post */}
      <div className="max-w-6xl mx-auto mb-16">
        <Link to={`/blog/${featuredPost.id}`} className="block">
          <div className="bg-[#FFF5EB] rounded-2xl overflow-hidden shadow-md transition-all hover:shadow-lg">
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-1/2 h-64 lg:h-auto relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80" 
                  alt="Restaurant staff working together" 
                  className="w-full h-full object-cover transition-transform hover:scale-105 duration-500"
                />
              </div>
              <div className="lg:w-1/2 p-8 flex flex-col justify-center">
                <div className="text-sm text-[#FF8C00] font-medium mb-2">{featuredPost.category}</div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-[#FF8C00] transition-colors">{featuredPost.title}</h2>
                <p className="text-gray-600 mb-6">{featuredPost.excerpt}</p>
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                    <img 
                      src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=776&q=80" 
                      alt={featuredPost.author} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium">{featuredPost.author}</p>
                    <p className="text-sm text-gray-500">{featuredPost.date}</p>
                  </div>
                </div>
                <div className="self-start px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors inline-block">
                  Read More
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
      
      {/* Blog Posts Grid */}
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-8">Recent Articles</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {recentPosts.map((post, index) => (
            <Link to={`/blog/${post.id}`} key={post.id} className="group border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="h-48 overflow-hidden relative">
                <img 
                  src={getImageUrlForPost(index)} 
                  alt={post.title} 
                  className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500"
                />
              </div>
              <div className="p-6">
                <div className="text-sm text-[#FF8C00] font-medium mb-2">{post.category}</div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-[#FF8C00] transition-colors">{post.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full overflow-hidden mr-2">
                      <img 
                        src={getAuthorImageUrl(index)} 
                        alt={post.author} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-sm">{post.author}</span>
                  </div>
                  <span className="text-sm text-gray-500">{post.date}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      
      {/* Newsletter Section */}
      <div className="max-w-6xl mx-auto mt-16 bg-gradient-to-r from-[#FF8C00]/90 to-[#FF8C00] rounded-xl p-8 md:p-12 text-center text-white">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">Subscribe to Our Newsletter</h2>
        <p className="mb-8 max-w-2xl mx-auto">
          Stay informed about the latest industry news, tips, and exclusive insights from the Hiree team.
        </p>
        <div className="flex flex-col sm:flex-row max-w-md mx-auto gap-4">
          <button 
            onClick={openRegistrationModal}
            className="px-6 py-3 bg-white text-[#FF8C00] font-medium rounded-lg hover:bg-gray-100 flex-grow"
          >
            Subscribe Now
          </button>
        </div>
      </div>
      
      {/* Registration Modal */}
      <EmailRegistrationModal 
        show={showRegistrationModal} 
        onClose={closeRegistrationModal} 
      />
    </div>
  );
}
